import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, MessageSquareQuote, ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";

const Stars = ({ n }) => (
  <span className="inline-flex items-center gap-0.5" aria-label={`${n}/5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`h-4 w-4 ${i <= n ? "fill-amber-400 text-amber-400" : "text-brand-ink/20"}`} />
    ))}
  </span>
);

// Homepage «Τι λένε οι πελάτες μας» — dev-managed reviews + Google review CTA
export const ReviewsSection = () => {
  const { t } = useLang();
  const r = t.reviews;
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reviews").then((res) => setData(res.data)).catch(() => setData({ reviews: [], google_url: "" }));
  }, []);

  if (!data) return null;
  const { reviews, google_url } = data;
  if (!reviews.length && !google_url) return null;

  return (
    <section id="reviews" className="relative py-24 sm:py-32" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-2xl">
            <span className="text-sm font-bold uppercase tracking-[0.2em] text-bamboo">{r.tag}</span>
            <h2 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-brand-ink tracking-tight leading-[1.05]">{r.title}</h2>
            <p className="mt-4 text-brand-muted leading-relaxed text-lg">{r.sub}</p>
          </div>
          {google_url && (
            <a
              href={google_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3.5 font-bold text-brand-cream shadow-[0_10px_30px_rgba(134,77,99,0.3)] hover:bg-brand-hover transition-colors duration-200 no-a11y-underline"
              data-testid="reviews-google-cta"
            >
              <Star className="h-5 w-5 fill-current" /> {r.google_cta} <ArrowUpRight className="h-4 w-4" />
            </a>
          )}
        </div>

        {reviews.length > 0 && (
          <div className="mt-12 flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:overflow-visible sm:pb-0" data-testid="reviews-list">
            {reviews.map((rev, i) => (
              <motion.figure
                key={rev.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
                className="snap-center shrink-0 w-[85vw] max-w-sm sm:w-auto sm:max-w-none rounded-[1.5rem] bg-white border border-brand/10 p-6 shadow-[0_8px_30px_rgba(134,77,99,0.08)]"
                data-testid={`review-card-${rev.id}`}
              >
                <MessageSquareQuote className="h-7 w-7 text-brand/30" />
                <blockquote className="mt-3 text-brand-ink/90 leading-relaxed whitespace-pre-wrap">{rev.text}</blockquote>
                <figcaption className="mt-4 flex items-center justify-between gap-3">
                  <span className="font-bold text-brand-ink">{rev.name}</span>
                  <Stars n={rev.rating} />
                </figcaption>
              </motion.figure>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
